import { useEffect, useRef } from "react"

import { drawFaceMesh, drawFinalFaceMesh } from "./draw-face-landmarks"
import type { FaceMeshCapture } from "./types"

export interface AnimatedFaceMeshProps {
  /** The captured detection to animate. Renders nothing when null. */
  capture: FaceMeshCapture | null
  className?: string
  /**
   * Mirror to match a mirrored selfie photo. When omitted, follows the capture's
   * own `mirrored` flag (selfie → true, gallery upload → false).
   */
  mirror?: boolean
  /** Total animation length (ms) before settling to the full mesh. Default 15000. */
  durationMs?: number
  /** Number of draw-on cycles across the duration. Default 10. */
  cycles?: number
}

/**
 * Canvas that animates the face mesh "drawing on" over a still photo: within each
 * cycle the connectors reveal 0→100%, repeated `cycles` times across `durationMs`
 * (the origene FaceProcessing "scanning" effect), then settles to the full static
 * mesh. Detection is never re-run — it replays a `FaceMeshCapture` from the camera.
 */
export function AnimatedFaceMesh({
  capture,
  className,
  mirror,
  durationMs = 15000,
  cycles = 10,
}: AnimatedFaceMeshProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !capture) return
    canvas.width = capture.videoWidth
    canvas.height = capture.videoHeight
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const shouldMirror = mirror ?? capture.mirrored ?? true
    const cycleDuration = durationMs / Math.max(cycles, 1)
    let raf = 0
    let start: number | null = null

    const animate = (ts: number) => {
      if (start === null) start = ts
      const elapsed = ts - start
      const total = Math.min(elapsed / durationMs, 1)

      if (total < 1) {
        const cycleProgress = (elapsed % cycleDuration) / cycleDuration
        drawFaceMesh(ctx, capture.results, canvas.width, canvas.height, {
          progress: cycleProgress,
          mirror: shouldMirror,
        })
        raf = requestAnimationFrame(animate)
      } else {
        drawFinalFaceMesh(ctx, capture.results, canvas.width, canvas.height, shouldMirror)
      }
    }

    raf = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(raf)
  }, [capture, mirror, durationMs, cycles])

  if (!capture) return null
  return <canvas ref={canvasRef} className={className} aria-hidden />
}
